<template>
  <div>
    <div class="reward-page">
      <ProgressiveMap
        ref="progressiveMapRef"
        @chest-position-updated="repositionChestAnimation"
        @map-loaded="loadAnimationsSequentially"
        :rewardContainer="rewardContainer"
      />

      <div class="reward-container no-select" ref="rewardContainer">
        <div class="reward-animation" ref="chestRewardAnimation" @click="handleChestClick" />
      </div>
      <div
        v-if="!starPopGameActive"
        class="star-animation no-select"
        ref="starRewardAnimation"
        :class="{ goToCounter: animateToCounter }"
      />
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import type { ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'

// Components
import ProgressiveMap from '@/components/ProgressiveMap.vue'

// Types
import type { ManagedTimeout, ActiveChestPosition } from '@/customTypes'

// Plugins
import lottie from 'lottie-web'

// Stores
import { useDataStore } from '@/stores/DataStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

const emit = defineEmits<{
  (e: 'animation-finished'): void
}>()

const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const dataStore = useDataStore()
const { screenerTemplateId, starPopGameActive } = storeToRefs(dataStore)
const effectsStore = useEffectsStore()
const { rewardAnimationTrigger } = storeToRefs(effectsStore)
const soundEffectsStore = useSoundEffectsStore()

const chestRewardAnimation = ref<HTMLElement | null>(null)
const starRewardAnimation = ref<HTMLElement | null>(null)
const rewardContainer = ref<HTMLElement | null>(null)
const progressiveMapRef = ref<InstanceType<typeof ProgressiveMap> | null>(null)

const transitionDuration: number = 400
const counterDestination = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const animateToCounter = ref<Boolean>(false)

// Animation state management
let chestAnim: any = null
let starAnim: any = null
const areAnimationsLoaded = ref<Boolean>(false)

const chestAnimation: ComputedRef<string> = computed(
  () =>
    `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/animations/ActiveChest.json?v=${_ASSETS_VERSION_}`
)

const starAnimation: ComputedRef<string> = computed(
  () =>
    `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/animations/ChestStarReward.json?v=${_ASSETS_VERSION_}`
)

type chestRewardAnimationSegment = {
  start: number
  end: number
}

// Animation segments for chest
const segments: chestRewardAnimationSegment[] = [
  { start: 0, end: 30 }, // Initial appearance
  { start: 60, end: 90 }, // Click hint
  { start: 120, end: 370 } // Chest opening
]

let currentSegment = 0
let currentSecondSegmentLoop = 0
const quantityToLoopSecondSegment = 3
let chestClicked = false

let chestClickTimeout = 2000
const minChestTimeout = 300
const decrementChestEachLoop = 500

const waitExpected = ref<Boolean>(true)
const waitForActionTimeout = ref<ManagedTimeout | null>(null)

// NOTE: Lifecycle

onUnmounted(() => {
  if (chestAnim) {
    chestAnim.removeEventListener('complete', onSegmentComplete)
    chestAnim.destroy()
  }
  if (starAnim) {
    starAnim.removeEventListener('complete', onStarComplete)
    starAnim.destroy()
  }
  starAnim = null
  chestAnim = null
})

// NOTE: Watchers
watch(rewardAnimationTrigger, (runAnimation) => {
  if (runAnimation) startChestAnimation()
})

watch(starPopGameActive, (isActive) => {
  if (isActive) {
    if (chestAnim) {
      onChestAnimationComplete()
    }
    if (starAnim) {
      starAnim.stop()
    }
  }
})

// NOTE: Methods

const loadAnimationsSequentially = async () => {
  await loadChestAnimation()
  await loadStarAnimation()
  areAnimationsLoaded.value = true
  if (rewardAnimationTrigger.value) {
    startChestAnimation()
  }
}

const loadChestAnimation = () => {
  return new Promise<void>((resolve) => {
    if (!chestRewardAnimation.value) {
      resolve()
      return
    }

    chestAnim = lottie.loadAnimation({
      container: chestRewardAnimation.value as HTMLElement,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: chestAnimation.value,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet'
      }
    })
    chestAnim.addEventListener('complete', onSegmentComplete)
    chestAnim.addEventListener('data_ready', () => {
      resolve()
    })
  })
}

const loadStarAnimation = () => {
  return new Promise<void>((resolve) => {
    if (!starRewardAnimation.value) {
      resolve()
      return
    }

    starAnim = lottie.loadAnimation({
      container: starRewardAnimation.value as HTMLElement,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: starAnimation.value,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet'
      }
    })
    starAnim.addEventListener('complete', onStarComplete)
    starAnim.addEventListener('data_ready', () => {
      resolve()
    })
  })
}

// Chest animation methods
const startChestAnimation = () => {
  if (!chestAnim || !areAnimationsLoaded.value) return
  if (progressiveMapRef.value) progressiveMapRef.value.checkActiveChestPosition()
  currentSegment = 0
  currentSecondSegmentLoop = 0
  chestClicked = false
  playCurrentSegment()
  effectsStore.setAvatarTransitionCallback(null, null)
  effectsStore.setRewardAnimationTrigger(false)
}

const repositionChestAnimation = (position: ActiveChestPosition) => {
  if (position && chestRewardAnimation.value) {
    chestRewardAnimation.value.style.left = `${position.x}px`
    chestRewardAnimation.value.style.top = `${position.y}px`
    chestRewardAnimation.value.style.width = `${position.width}px`
  }
  getCounterDestination()
}

const playCurrentSegment = () => {
  const segment = segments[currentSegment]
  soundEffectsStore.playTrack(`reward-step-${currentSegment}`)
  chestAnim.playSegments([segment.start, segment.end], true)

  // if chest playing opening animation play star animation
  if (currentSegment === 2) {
    playStarAnimation()
  }
}

const playStarAnimation = () => {
  if (starAnim) {
    starAnim.play()
  } else {
    loadStarAnimation().then(() => {
      starAnim.play()
    })
  }
}

const onSegmentComplete = () => {
  if (currentSegment === 0 && waitExpected.value) {
    waitForActionTimeout.value = setManagedTimeout(() => {
      waitExpected.value = false
      onSegmentComplete()
    }, 1000)
  } else if (
    currentSegment === 1 &&
    currentSecondSegmentLoop < quantityToLoopSecondSegment &&
    !chestClicked
  ) {
    waitForActionTimeout.value = setManagedTimeout(() => {
      currentSecondSegmentLoop++
      waitExpected.value = true
      // Chest Click animation speed increases over time until the chest opens or is clicked
      chestAnim.setSpeed(currentSecondSegmentLoop + 1)
      playCurrentSegment()

      chestClickTimeout = Math.max(minChestTimeout, chestClickTimeout - decrementChestEachLoop)
    }, chestClickTimeout)
  } else if (
    currentSegment === 1 &&
    currentSecondSegmentLoop >= quantityToLoopSecondSegment &&
    !chestClicked &&
    waitExpected.value
  ) {
    chestAnim.setSpeed(1)
    waitForActionTimeout.value = setManagedTimeout(() => {
      waitExpected.value = false
      onSegmentComplete()
    }, 1000)
  } else {
    currentSegment++
    if (currentSegment < segments.length) {
      playCurrentSegment()
    } else {
      onChestAnimationComplete()
    }
  }
}

const handleChestClick = () => {
  if (currentSegment === 1) {
    clearManagedTimeout(waitForActionTimeout)
    chestClicked = true
    currentSegment++
    chestAnim.setSpeed(1)
    playCurrentSegment()
  }
}

const onChestAnimationComplete = () => {
  chestAnim.playSegments([350, 370], true)
  nextTick(() => {
    chestAnim.goToAndStop(360, false)
  })
}

// Star animation methods
const onStarComplete = () => {
  dataStore.addStar(10)
  dataStore.sendRewardUpdateToApi(10)
  animateToCounter.value = true
  emit('animation-finished')
}

const getCounterDestination = () => {
  const counterElement: HTMLElement | null = document.getElementsByClassName(
    'starCounterAnimationHolder'
  )?.[0] as HTMLElement
  if (!counterElement) return
  const rect = counterElement.getBoundingClientRect()

  // Check the orientation and calculate the position accordingly
  if (window.innerWidth > window.innerHeight) {
    const positionFromLeft = window.innerWidth - rect.left - counterElement.offsetWidth / 2
    const positionFromTop = ((counterElement.offsetHeight + rect.bottom) / 2) * -1
    counterDestination.value.x = positionFromLeft
    counterDestination.value.y = positionFromTop
  } else {
    const positionFromLeft = rect.left - window.innerWidth
    const positionFromTop = window.innerHeight - rect.top - counterElement.offsetWidth / 2
    counterDestination.value.x = positionFromTop
    counterDestination.value.y = positionFromLeft
  }
}

defineExpose({
  getPathForAvatar: async () => {
    await nextTick()
    return progressiveMapRef.value?.getPathForAvatar()
  },
  startChestAnimation
})
</script>

<style lang="scss" scoped>
.reward-page {
  width: var(--full-vw);
  height: var(--full-vh);
  overflow: hidden;
  background-color: #ffffff;

  .reward-container {
    position: absolute;
    z-index: 1016;

    .reward-animation {
      position: absolute;
      width: 100px;
      transform: translate3d(-50%, calc(-70%), 0px);
      cursor: pointer;
    }
  }

  .star-animation {
    position: absolute;
    width: var(--full-vw);
    height: var(--full-vh);
    left: 0;
    top: 0;
    z-index: 1016;
    pointer-events: none;
    &.goToCounter {
      @include animation(flyOff v-bind('`${transitionDuration}ms`') forwards);
    }
  }
}
@include keyframes(flyOff) {
  0% {
    transform: translate(0px, 0px);
  }
  80% {
    opacity: 1;
  }
  100% {
    transform: translate(
        calc(var(--full-vw) * 0.5 - v-bind('`${counterDestination.x}px`')),
        calc((var(--full-vh) * -0.5) - v-bind('`${counterDestination.y}px`'))
      )
      scale(0.1);
    opacity: 0;
  }
}
</style>
